import React, { useState, useEffect } from 'react';
import { Users, Award, Target, Heart, Linkedin, Music, Instagram, MessageCircle, Twitter, Github, Globe, Mail, Star, ArrowRight, Sparkles, X, Calendar, Trophy, Quote, Briefcase, ChevronRight, ShieldCheck } from 'lucide-react';
import { teamMembers, staffMembers } from '../mock';
import { Badge } from '../components/ui/badge';
import { getImageUrl } from '../utils/imageUtils';
import axios from 'axios';
import { ExecutiveCard } from '../components/team/ExecutiveCard';
import { ExecutiveProfileDrawer } from '../components/team/ExecutiveProfileDrawer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Team = () => {
  const [staffProfiles, setStaffProfiles] = useState({});
  const [execOverrides, setExecOverrides] = useState({});
  const [staffOverrides, setStaffOverrides] = useState({});
  const [loading, setLoading] = useState(true);
  const [hoveredExec, setHoveredExec] = useState(null);
  const [hoveredStaff, setHoveredStaff] = useState(null);
  const [failedImages, setFailedImages] = useState({});
  const [selectedExec, setSelectedExec] = useState(null);
  const [activeDept, setActiveDept] = useState('all');

  const handleImageError = (key) => {
    setFailedImages(prev => ({ ...prev, [key]: true }));
  };

  useEffect(() => {
    fetchStaffProfiles();
    fetchExecOverrides();
    fetchStaffOverrides();
  }, []);

  const fetchExecOverrides = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/executive-profiles`);
      const map = {};
      (response.data || []).forEach(p => { if (p.exec_id) map[p.exec_id] = p; });
      setExecOverrides(map);
    } catch (err) {
      // silent — falls back to static mock data
    }
  };

  const fetchStaffOverrides = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/staff-profile-overrides`);
      const map = {};
      (response.data || []).forEach(p => { if (p.staff_id) map[p.staff_id] = p; });
      setStaffOverrides(map);
    } catch (err) {
      // silent — falls back to static mock data
    }
  };

  const mergeExec = (m) => {
    const o = execOverrides[m.id] || {};
    return {
      ...m,
      _hidden: o.hidden === true,
      name: o.name ?? m.name,
      role: o.role ?? m.role,
      roleShort: o.role_short ?? m.roleShort,
      avatar: o.avatar ?? m.avatar,
      bio: o.bio ?? m.bio,
      fullBio: o.full_bio ?? m.fullBio,
      focusAreas: (o.focus_areas && o.focus_areas.length) ? o.focus_areas : m.focusAreas,
      joinedYear: o.joined_year ?? m.joinedYear,
      achievements: (o.achievements && o.achievements.length) ? o.achievements : m.achievements,
      quote: o.quote ?? m.quote,
      contact: o.show_email && o.contact_email ? { email: o.contact_email } : null,
    };
  };

  // DB-only execs (added by CEO, not in static mock)
  const baseIds = new Set(teamMembers.map(m => m.id));
  const dbOnlyExecs = Object.values(execOverrides)
    .filter(o => !baseIds.has(o.exec_id) && !o.hidden)
    .map(o => ({
      id: o.exec_id,
      name: o.name || 'Unnamed',
      role: o.role || '',
      roleShort: o.role_short || '',
      avatar: o.avatar || '',
      bio: o.bio || '',
      fullBio: o.full_bio || '',
      focusAreas: o.focus_areas || [],
      joinedYear: o.joined_year,
      achievements: o.achievements || [],
      quote: o.quote || '',
      contact: o.show_email && o.contact_email ? { email: o.contact_email } : null,
    }));

  const allExecs = [...teamMembers.map(mergeExec).filter(m => !m._hidden), ...dbOnlyExecs];

  const fetchStaffProfiles = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/staff-profiles`);
      const profileMap = {};
      response.data.forEach(profile => {
        if (profile.username) {
          profileMap[profile.username.toLowerCase()] = profile;
        }
      });
      setStaffProfiles(profileMap);
    } catch (error) {
      console.error('Error fetching staff profiles:', error);
    } finally {
      setLoading(false);
    }
  };

  const getMergedStaffData = () => {
    // Build quick-lookup map by linked_username so overrides created for real
    // staff accounts (via self-publish or auto-create) can be matched to their
    // uploaded avatar reliably — this is the fix for "photo added but nothing
    // changes, still shows a CC picture".
    const overridesByUsername = {};
    Object.values(staffOverrides).forEach(o => {
      if (o.linked_username) overridesByUsername[o.linked_username.toLowerCase()] = o;
    });

    // Start with base mocked staff
    const merged = staffMembers.map(mockStaff => {
      const mockNameLc = mockStaff.name.toLowerCase();
      const mockFirst = mockStaff.name.split(/\s+/)[0].toLowerCase();
      const matchingProfile = Object.values(staffProfiles).find(profile => {
        const dn = (profile.display_name || '').toLowerCase();
        const un = (profile.username || '').toLowerCase();
        if (!dn && !un) return false;
        // Exact match on either side
        if (dn === mockNameLc) return true;
        if (un === mockNameLc.replace(/\s+/g, '')) return true;
        // Fuzzy: first-name equality OR mock name contained in display_name
        if (dn && (dn.startsWith(mockFirst) || dn.includes(mockNameLc))) return true;
        if (un && un.replace(/[._-]/g, '').startsWith(mockFirst)) return true;
        return false;
      });
      const override = staffOverrides[mockStaff.id] || {};
      const base = matchingProfile
        ? {
            ...mockStaff,
            avatar: matchingProfile.avatar || mockStaff.avatar || null,
            bio: matchingProfile.bio || mockStaff.bio,
            contact_email: matchingProfile.contact_email || null,
            socials: matchingProfile.socials || null,
            hasProfile: true,
          }
        : { ...mockStaff, hasProfile: false };
      // Apply superadmin overrides on top
      return {
        ...base,
        name: override.name ?? base.name,
        role: override.role ?? base.role,
        department: override.department ?? base.department ?? 'other',
        bio: override.bio ?? base.bio,
        avatar: override.avatar ?? base.avatar,
        contact_email: override.contact_email ?? base.contact_email,
        socials: override.socials ?? base.socials,
        tags: (override.tags && override.tags.length) ? override.tags : base.tags,
        _hidden: override.hidden === true,
      };
    });

    // Custom staff added by CEO (staff_id > 100) OR auto-added when a new
    // staff account is created. Prefer their live admin_users profile (avatar/
    // bio/socials) over stored override so a fresh upload takes effect right
    // away without needing an admin to touch anything.
    const customStaff = Object.values(staffOverrides)
      .filter(o => o.staff_id > 100 && o.is_custom && !o.hidden)
      .map(o => {
        const linked = o.linked_username
          ? staffProfiles[o.linked_username.toLowerCase()] || null
          : null;
        return {
          id: o.staff_id,
          name: o.name || (linked && linked.display_name) || 'Unnamed',
          role: o.role || '',
          department: o.department || 'other',
          bio: (linked && linked.bio) || o.bio || '',
          initials: o.initials || (o.name || 'U').split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase(),
          avatar: (linked && linked.avatar) || o.avatar || null,
          contact_email: (linked && linked.contact_email) || o.contact_email || null,
          socials: (linked && linked.socials) || o.socials || null,
          tags: o.tags || [],
          hasProfile: !!linked,
        };
      });

    return [...merged, ...customStaff].filter(s => !s._hidden);
  };

  const mergedStaff = getMergedStaffData();

  const teamStats = [
    { label: "Years Experience", value: "3+", icon: Award },
    { label: "Groups Managed", value: "10+", icon: Target },
    { label: "Team Members", value: staffMembers.length + teamMembers.length, icon: Users },
    { label: "Passion", value: "100%", icon: Heart }
  ];

  const getSocialIcon = (platform) => {
    const icons = { linkedin: Linkedin, discord: MessageCircle, instagram: Instagram, spotify: Music, twitter: Twitter, github: Github, website: Globe };
    return icons[platform] || Globe;
  };

  const getSocialLink = (platform, value) => {
    if (!value) return '#';
    const prefixes = { linkedin: 'https://linkedin.com/in/', instagram: 'https://instagram.com/', twitter: 'https://twitter.com/', github: 'https://github.com/', spotify: '', discord: '', website: '' };
    if (value.startsWith('http')) return value;
    if (platform === 'discord') return '#';
    return prefixes[platform] + value.replace(/^[@\/]/, '');
  };

  return (
    <div className="min-h-screen bg-[#05050A]" data-testid="team-page" style={{ fontFamily: "'Inter', sans-serif" }}>

      {/* Hero Section */}
      <section className="relative pt-32 pb-24 overflow-hidden">
        {/* Ambient glow effects */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/4 left-1/3 w-[600px] h-[600px] bg-[#00E5FF]/[0.04] rounded-full blur-[150px]" />
          <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-[#8A2BE2]/[0.04] rounded-full blur-[120px]" />
          <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
        </div>

        <div className="relative z-10 max-w-5xl mx-auto px-6 lg:px-8 text-center">
          <div
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-white/10 bg-white/[0.03] backdrop-blur-md mb-8"
            style={{ animation: 'fadeInUp 0.6s ease-out' }}
          >
            <Sparkles className="w-3.5 h-3.5 text-[#00E5FF]" />
            <span className="text-xs tracking-[0.2em] uppercase text-white/50 font-medium">Our People</span>
          </div>

          <h1
            className="text-4xl sm:text-5xl lg:text-6xl font-light tracking-tighter text-white mb-6"
            style={{ animation: 'fadeInUp 0.6s ease-out 0.1s both' }}
          >
            Meet the{' '}
            <span className="bg-gradient-to-r from-[#00E5FF] to-[#8A2BE2] bg-clip-text text-transparent font-medium">
              Team
            </span>
          </h1>

          <p
            className="text-base md:text-lg text-white/40 max-w-xl mx-auto leading-relaxed"
            style={{ animation: 'fadeInUp 0.6s ease-out 0.2s both' }}
          >
            Dedicated professionals united by a shared passion for excellence in everything we do.
          </p>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="py-12 border-y border-white/[0.04]">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {teamStats.map((stat, index) => (
              <div
                key={index}
                className="text-center group"
                style={{ animation: `fadeInUp 0.5s ease-out ${0.1 * index}s both` }}
                data-testid={`stat-${stat.label.toLowerCase().replace(/\s/g, '-')}`}
              >
                <div className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-white/[0.03] border border-white/[0.06] mb-3 group-hover:border-[#00E5FF]/20 transition-colors">
                  <stat.icon className="w-4 h-4 text-white/30 group-hover:text-[#00E5FF]/70 transition-colors" />
                </div>
                <p className="text-3xl md:text-4xl font-light tracking-tight bg-gradient-to-r from-[#00E5FF] to-[#8A2BE2] bg-clip-text text-transparent">
                  {stat.value}
                </p>
                <p className="text-[11px] tracking-[0.15em] uppercase text-white/30 mt-1">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Executive Team */}
      <section className="py-24">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-white/10 bg-white/[0.03] mb-5">
              <Star className="w-3 h-3 text-[#00E5FF]" />
              <span className="text-[10px] tracking-[0.2em] uppercase text-white/40 font-medium">Leadership</span>
            </div>
            <h2 className="text-2xl md:text-3xl font-light tracking-tight text-white" data-testid="exec-section-title">
              Executive Team
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {allExecs.map((member, index) => <ExecutiveCard key={member.id} member={member} index={index} onOpen={setSelectedExec} />)}
          </div>
        </div>
      </section>

      {/* Core Staff */}
      <section className="py-24 relative">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[400px] bg-[#8A2BE2]/[0.02] rounded-full blur-[150px]" />
        </div>

        <div className="relative z-10 max-w-6xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-white/10 bg-white/[0.03] mb-5">
              <Users className="w-3 h-3 text-[#8A2BE2]" />
              <span className="text-[10px] tracking-[0.2em] uppercase text-white/40 font-medium">Core Team</span>
            </div>
            <h2 className="text-2xl md:text-3xl font-light tracking-tight text-white" data-testid="staff-section-title">
              Our Staff
            </h2>
          </div>

          {/* Department tabs */}
          {!loading && (() => {
            const depts = [
              { key: 'all',   label: 'All', color: '#00E5FF' },
              { key: 'ops',   label: 'Operations', color: '#00E5FF' },
              { key: 'people', label: 'People', color: '#8A2BE2' },
              { key: 'community', label: 'Community', color: '#00E5FF' },
              { key: 'executive-support', label: 'Exec Support', color: '#8A2BE2' },
            ];
            const counts = mergedStaff.reduce((acc, s) => {
              const d = s.department || 'other';
              acc[d] = (acc[d] || 0) + 1;
              acc.all = (acc.all || 0) + 1;
              return acc;
            }, {});
            return (
              <div className="flex items-center justify-center gap-1.5 mb-12 flex-wrap" data-testid="dept-tabs">
                {depts.filter(d => d.key === 'all' || counts[d.key]).map(d => (
                  <button key={d.key} onClick={() => setActiveDept(d.key)} data-testid={`dept-tab-${d.key}`}
                    className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-[10px] tracking-[0.12em] uppercase font-medium border transition-all ${
                      activeDept === d.key
                        ? 'text-white border-white/20 bg-white/[0.05]'
                        : 'text-white/40 border-white/[0.06] bg-white/[0.02] hover:text-white/70 hover:border-white/[0.12]'
                    }`}
                    style={activeDept === d.key ? { boxShadow: `0 0 20px -5px ${d.color}30`, borderColor: `${d.color}30` } : undefined}>
                    <span className={activeDept === d.key ? '' : ''} style={activeDept === d.key ? { color: d.color } : undefined}>
                      {d.label}
                    </span>
                    <span className={`px-1.5 py-0.5 rounded-full text-[9px] tabular-nums ${activeDept === d.key ? 'bg-white/10 text-white/70' : 'bg-white/[0.06] text-white/40'}`}>
                      {counts[d.key] || 0}
                    </span>
                  </button>
                ))}
              </div>
            );
          })()}

          {loading ? (
            <div className="flex justify-center py-16">
              <div className="w-8 h-8 rounded-full border border-white/10 border-t-[#00E5FF] animate-spin" />
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6" data-testid={`staff-grid-${activeDept}`}>
              {mergedStaff.filter(s => activeDept === 'all' || (s.department || 'other') === activeDept).map((staff, index) => (
                <div
                  key={index}
                  data-testid={`staff-card-${staff.name.toLowerCase().replace(/\s+/g, '-')}`}
                  className="group relative rounded-2xl overflow-hidden border border-white/[0.06] bg-[#0A0A12] transition-all duration-500 hover:-translate-y-1 hover:border-[#00E5FF]/25 p-6 pt-7"
                  onMouseEnter={() => setHoveredStaff(index)}
                  onMouseLeave={() => setHoveredStaff(null)}
                  style={{
                    animation: `fadeInUp 0.45s ease-out ${0.06 * index}s both`,
                    boxShadow: hoveredStaff === index ? '0 0 40px rgba(0,229,255,0.08)' : 'none'
                  }}
                >
                  {/* Verified badge top-right (jewel corner) */}
                  {staff.hasProfile && (
                    <div className="absolute top-3 right-3 w-7 h-7 rounded-lg bg-white/[0.03] backdrop-blur-md border border-white/10 flex items-center justify-center" title="Verified profile">
                      <svg className="w-3 h-3 text-emerald-400/80" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                  )}

                  <div className="flex flex-col items-center text-center">
                    {/* Avatar — luxury circular with online dot + cyan halo pulse on hover */}
                    <div className="relative mb-4">
                      <div className="w-24 h-24 rounded-full overflow-hidden relative"
                        style={{
                          background: 'radial-gradient(circle at 30% 30%, rgba(0,229,255,0.08), rgba(10,10,18,1) 70%)',
                          boxShadow: hoveredStaff === index
                            ? '0 0 40px -5px rgba(0,229,255,0.35), inset 0 0 0 1px rgba(0,229,255,0.35)'
                            : '0 0 30px -5px rgba(0,229,255,0.15), inset 0 0 0 1px rgba(0,229,255,0.15)',
                          transition: 'box-shadow 500ms ease'
                        }}>
                        {staff.avatar && !failedImages[`staff-${index}`] ? (
                          <img
                            src={getImageUrl(staff.avatar)}
                            alt={staff.name}
                            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                            onError={() => handleImageError(`staff-${index}`)}
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <span className="text-2xl font-light text-white/25">
                              {staff.initials || staff.name.split(' ').map(n => n[0]).join('')}
                            </span>
                          </div>
                        )}
                      </div>
                      {/* Team-member dot */}
                      {staff.hasProfile && (
                        <span className="absolute bottom-1 right-1 w-3.5 h-3.5 rounded-full bg-emerald-400 border-2 border-[#0A0A12]" title="Active team member" />
                      )}
                    </div>

                    {/* Info */}
                    <h3 className="text-base font-medium text-white mb-1 tracking-tight">{staff.name}</h3>
                    <p className="text-[10px] tracking-[0.15em] uppercase text-[#00E5FF]/70 mb-2">{staff.role}</p>
                    {/* Subtle divider */}
                    <div className="w-8 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent mb-3" />
                    {staff.tags?.length > 0 && (
                      <div className="flex flex-wrap justify-center gap-1 mb-2">
                        {staff.tags.map((t, i) => (
                          <span key={i} data-testid={`staff-tag-${staff.name.toLowerCase().replace(/\s+/g, '-')}-${i}`}
                            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[10px] tracking-[0.08em] font-medium border ${
                              t.color === 'violet' ? 'bg-[#1a1220]/60 text-[#c39be5]/90 border-[#8A2BE2]/15'
                              : t.color === 'red' ? 'bg-[#1a1212]/60 text-red-300/90 border-red-500/15'
                              : t.color === 'amber' ? 'bg-[#1a1712]/60 text-amber-300/90 border-amber-500/15'
                              : 'bg-[#111a1c]/60 text-[#00E5FF]/90 border-[#00E5FF]/15'
                            }`}>
                            <ShieldCheck className="w-2.5 h-2.5" strokeWidth={2} />
                            {t.label}
                          </span>
                        ))}
                      </div>
                    )}
                    <p className="text-xs text-white/40 leading-relaxed line-clamp-2 mb-4 min-h-[32px]">{staff.bio}</p>

                    {/* Socials */}
                    {(staff.contact_email || (staff.socials && Object.entries(staff.socials).some(([, value]) => value))) && (
                      <div className="flex flex-wrap justify-center gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        {staff.contact_email && (
                          <a
                            href={`mailto:${staff.contact_email}`}
                            className="p-1.5 rounded-md bg-white/[0.03] hover:bg-[#00E5FF]/10 border border-white/[0.04] hover:border-[#00E5FF]/20 transition-all"
                            title="Email"
                            data-testid={`social-email-${staff.name.toLowerCase().replace(/\s+/g, '-')}`}
                          >
                            <Mail className="w-3 h-3 text-white/30 hover:text-[#00E5FF]" />
                          </a>
                        )}
                        {staff.socials && Object.entries(staff.socials).map(([platform, value]) => {
                          if (!value) return null;
                          const Icon = getSocialIcon(platform);
                          return (
                            <a
                              key={platform}
                              href={getSocialLink(platform, value)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="p-1.5 rounded-md bg-white/[0.03] hover:bg-[#00E5FF]/10 border border-white/[0.04] hover:border-[#00E5FF]/20 transition-all"
                              title={platform}
                            >
                              <Icon className="w-3 h-3 text-white/30 hover:text-[#00E5FF]" />
                            </a>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* Bottom accent line on hover (jewel signature) */}
                  <div className="absolute bottom-0 left-6 right-6 h-px bg-gradient-to-r from-transparent via-[#00E5FF]/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Join CTA */}
      <section className="py-24 relative">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          <div className="relative rounded-2xl border border-white/[0.06] bg-white/[0.02] backdrop-blur-sm overflow-hidden" data-testid="join-cta">
            {/* Glow effects */}
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-64 h-32 bg-[#00E5FF]/[0.04] rounded-full blur-[80px]" />
            <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-48 h-24 bg-[#8A2BE2]/[0.04] rounded-full blur-[60px]" />

            <div className="relative p-12 md:p-16 text-center">
              <h2 className="text-xl md:text-2xl font-light tracking-tight text-white mb-3">
                Want to{' '}
                <span className="bg-gradient-to-r from-[#00E5FF] to-[#8A2BE2] bg-clip-text text-transparent">
                  Join
                </span>{' '}
                Our Team?
              </h2>
              <p className="text-sm text-white/30 mb-8 max-w-md mx-auto">
                We're always looking for talented individuals to join our growing team.
              </p>
              <a
                href="/vacancies"
                className="inline-flex items-center gap-2 bg-white text-black font-medium text-sm px-7 py-3 rounded-lg hover:bg-white/90 transition-all group"
                data-testid="view-positions-btn"
              >
                View Open Positions
                <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Bottom gradient line */}
      <div className="h-px bg-gradient-to-r from-transparent via-white/5 to-transparent" />

      {/* CSS Animations */}
      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes drawerIn {
          from { opacity: 0; transform: translateX(60px); }
          to { opacity: 1; transform: translateX(0); }
        }
        @keyframes modalIn {
          from { opacity: 0; transform: translateY(20px) scale(0.96); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>

      {selectedExec !== null && allExecs[selectedExec] && (
        <ExecutiveProfileDrawer execs={allExecs} index={selectedExec} onClose={() => setSelectedExec(null)} onNavigate={setSelectedExec} />
      )}
    </div>
  );
};

export default Team;
